// ── Dictionary entry (matches dictionary_entries table) ───────────────────────

export type EntryStatus =
  | 'candidate_high_priority'
  | 'candidate_unverified'
  | 'needs_review'
  | 'candidate_low_priority'

export type EntryType =
  | 'lexical_candidate'
  | 'grammar_marker'
  | 'phrase_candidate'
  | 'proper_noun'
  | 'uncertain'

export type SuggestionType =
  | 'better_tamang'
  | 'better_nepali'
  | 'better_english'
  | 'romanization'
  | 'dialect_variant'
  | 'wrong_entry'
  | 'other'

export type ReviewStatus = 'pending' | 'accepted' | 'rejected' | 'merged'

export interface DictionaryEntry {
  id: number
  entry_id: string
  entry_type: EntryType
  tamang_form_surface: string
  tamang_form_normalized: string
  tamang_variant_note: string
  nepali_form: string
  english_gloss_auto: string
  english_gloss_candidates: string
  romanized_tamang: string
  ipa_tamang: string
  frequency: number

  example_tamang_1: string
  example_nepali_1: string
  example_english_1_auto: string
  example_sentence_id_1: string

  example_tamang_2: string
  example_nepali_2: string
  example_english_2_auto: string
  example_sentence_id_2: string

  example_tamang_3: string
  example_nepali_3: string
  example_english_3_auto: string
  example_sentence_id_3: string

  alignment_sources: string
  has_position_support: boolean
  has_eflomal_support: boolean
  has_tamang_neural_support: boolean
  alignment_consistency_score: number | null
  has_true_roundtrip_score: boolean
  status: EntryStatus
  status_rank: number
}

// Lightweight version returned by search RPC
export type EntrySearchRow = Pick<
  DictionaryEntry,
  | 'id'
  | 'entry_id'
  | 'entry_type'
  | 'tamang_form_surface'
  | 'tamang_form_normalized'
  | 'tamang_variant_note'
  | 'nepali_form'
  | 'english_gloss_auto'
  | 'romanized_tamang'
  | 'ipa_tamang'
  | 'frequency'
  | 'alignment_sources'
  | 'has_eflomal_support'
  | 'has_tamang_neural_support'
  | 'alignment_consistency_score'
  | 'status'
  | 'status_rank'
> & { total_count: number }

// ── Suggestion ─────────────────────────────────────────────────────────────

export interface Suggestion {
  id: string
  entry_id: string
  submitter_name: string | null
  submitter_email: string | null
  suggestion_type: SuggestionType
  corrected_tamang: string | null
  corrected_nepali: string | null
  corrected_english: string | null
  suggested_romanization: string | null
  dialect_note: string | null
  speaker_notes: string | null
  submitted_at: string
  review_status: ReviewStatus
  reviewed_by: string | null
  reviewed_at: string | null
  admin_notes: string | null
}

export interface SuggestionWithEntry extends Suggestion {
  entry: DictionaryEntry
}

// ── New suggestion form payload ─────────────────────────────────────────────

export interface SuggestionPayload {
  entry_id: string
  submitter_name: string
  submitter_email: string
  suggestion_type: SuggestionType
  corrected_tamang: string
  corrected_nepali: string
  corrected_english: string
  suggested_romanization: string
  dialect_note: string
  speaker_notes: string
}

// ── Supabase database type shim (enough for our tables) ─────────────────────

export type Database = {
  public: {
    Tables: {
      dictionary_entries: {
        Row: DictionaryEntry
        Insert: Omit<DictionaryEntry, 'id' | 'status_rank'>
        Update: Partial<Omit<DictionaryEntry, 'id' | 'status_rank'>>
      }
      suggestions: {
        Row: Suggestion
        Insert: Omit<Suggestion, 'id' | 'submitted_at' | 'review_status' | 'reviewed_by' | 'reviewed_at' | 'admin_notes'>
        Update: Partial<Suggestion>
      }
    }
    Functions: {
      search_entries: {
        Args: { search_term: string; page_offset?: number; page_limit?: number }
        Returns: EntrySearchRow[]
      }
    }
  }
}

// ── Display helpers ─────────────────────────────────────────────────────────

export const STATUS_LABELS: Record<EntryStatus, string> = {
  candidate_high_priority: 'High Priority',
  candidate_unverified:    'Unverified',
  needs_review:            'Needs Review',
  candidate_low_priority:  'Low Priority',
}

export const TYPE_LABELS: Record<EntryType, string> = {
  lexical_candidate: 'Lexical',
  grammar_marker:    'Grammar',
  phrase_candidate:  'Phrase',
  proper_noun:       'Proper Noun',
  uncertain:         'Uncertain',
}

export const SUGGESTION_TYPE_LABELS: Record<SuggestionType, string> = {
  better_tamang:    'Better Tamang translation',
  better_nepali:    'Better Nepali form',
  better_english:   'Better English gloss',
  romanization:     'Romanized Tamang (Latin script)',
  dialect_variant:  'Dialect variant / alternate form',
  wrong_entry:      'This entry is wrong or a duplicate',
  other:            'Other note / comment',
}
